
const PORTAL_BASE = (import.meta.env.CLAIMSHIELD_PORTAL_BASE_URL || 'https://portal.claimshieldpro.com')
/**
 * Consultant how-to guides shown under /guides in the ops portal.
 *
 * Each guide is a self-contained HTML file in `public/guides/` (served static at
 * the domain root, e.g. /guides/<file>). To add a guide:
 *   1. Drop the self-contained HTML into `public/guides/`.
 *   2. Add an entry below.
 *   3. Rebuild + deploy the ops package.
 * The `howto-guide` skill produces guides in exactly this self-contained form.
 */
export interface GuideMeta {
  /** URL slug — the route is /guides/<slug>. */
  slug: string
  title: string
  /** One-line description shown on the card. */
  description: string
  /** Grouping label (shown as a badge). */
  category: string
  /** Path to the static HTML under public/, served at the domain root. */
  file: string
  /**
   * True when `file` is an ABSOLUTE URL on another origin.
   *
   * One guide is hosted by the portal rather than by ops: the client quickstart,
   * so that the link a carrier follows stays on portal.claimshieldpro.com — the
   * host they already sign in to — instead of advertising our internal ops
   * domain. There is still exactly one copy of the file.
   *
   * The viewer cannot iframe it (ops CSP is `frame-src 'self'`), so it opens in
   * a new tab instead. Anything cross-origin MUST set this: without it the frame
   * silently renders blank, which looks like a missing guide.
   */
  external?: boolean
}

export const GUIDES: GuideMeta[] = [
  {
    slug: 'historical-cdl-upload',
    title: "Keeping a Driver's Original (Expired) CDL on File",
    description:
      "File a driver's original or already-expired license as a historical copy — without changing their current license status or setting off expiration alerts.",
    category: 'Driver Files',
    file: '/guides/historical-cdl-upload.html',
  },
  {
    slug: 'driver-file-share',
    title: "Sharing a Driver's File With a Client",
    description:
      "Turn a driver's documents into one secure link to send a client — they view or download any document, or get everything as one branded PDF. No login needed; the link expires and is revocable.",
    category: 'Driver Files',
    file: '/guides/driver-file-share.html',
  },
  {
    slug: 'applicant-prescreen',
    title: 'Pre-Screening a Job Applicant',
    description:
      'How to screen a job applicant: make the quick phone call, book the in-person interview, and send the 5-question form that lands their answers on the record.',
    category: 'Hiring',
    file: '/guides/applicant-prescreen.html',
  },
  {
    slug: 'fmcsa-portal-access',
    title: "Getting Added to a Client's FMCSA Portal",
    description:
      'Request a role on a client’s USDOT so we can file DataQs, keep their MCS-150 current, and see their real federal record — without ever holding their password. Covers both client journeys and where it stalls.',
    category: 'Client Onboarding',
    file: '/guides/fmcsa-portal-access.html',
  },
  {
    slug: 'fmcsa-portal-access-client',
    title: 'Giving ClaimShield Access to Your FMCSA Portal',
    description:
      'CLIENT-FACING — send this one to the carrier. Walks their Company Official through Login.gov, FMCSA Portal registration, and approving our access request.',
    category: 'Client Onboarding',
    file: '/guides/fmcsa-portal-access-client.html',
  },
  {
    slug: 'portal-walkthrough-consultant',
    title: 'Walking a Client Through the Portal',
    description:
      'The walkthrough-call script — what to show, in what order, and the answers to the "can it do X?" questions where the answer is a deliberate no (ad-hoc MVR ordering, driver LMS resets, "does this replace you?").',
    category: 'Portal Rollout',
    file: '/guides/portal-walkthrough-consultant.html',
  },
  {
    slug: 'your-claimshield-portal',
    title: 'Your ClaimShield Portal',
    description:
      'CLIENT-FACING — the leave-behind after the walkthrough. Signing in, the three recurring jobs (upload a document, answer a request, check safety scores), and how to reach you. Hosted on the portal, so the link stays on a domain the client recognises.',
    category: 'Portal Rollout',
    file: `${PORTAL_BASE}/guides/your-claimshield-portal.html`,
    external: true,
  },
  {
    slug: 'welcome-call',
    title: 'The Welcome Call — Booked and Held',
    description:
      'How the welcome call gets booked today (the client self-books from their welcome email), what to do when it never was, and why booked and held are tracked separately.',
    category: 'Client Onboarding',
    file: '/guides/welcome-call.html',
  },
]

export function findGuide(slug: string): GuideMeta | undefined {
  return GUIDES.find((g) => g.slug === slug)
}
